/**
 * State for one framed gateway page (#939, Feature H): the frame's address, what it has told
 * the Marketplace so far, its height, and a way to load it again.
 *
 * A frame is "loading" until its page says `ready`. A signed-out user is sent to a sign-in
 * page that refuses framing, so for them `ready` never comes, and the state becomes
 * "no-response". Each caller decides what that means (My Connections offers sign-in; the
 * recent-use box simply stays hidden).
 *
 * Two things end the wait. A page that is signed in posts `ready` while it is still loading, so
 * a frame that has finished loading (the iframe's load event, which also fires for a page the
 * browser refused to frame) and says nothing within FRAME_LOAD_GRACE_MS is not going to. And
 * FRAME_TIMEOUT_MS covers a frame that never finishes loading. A `ready` that does arrive late
 * still wins, so a slow gateway only shows the sign-in prompt briefly.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { manageFrameUrl, parseGatewayMessage, type ManagePage } from "./mcp-gateway";
import { reportEvent } from "./newrelic";
import { useResolvedTheme } from "./theme-context";

/** How long to wait for `ready` before treating the page as not answering, however the frame is doing. */
export const FRAME_TIMEOUT_MS = 10000;

/** How long to wait for `ready` after the frame has loaded. */
export const FRAME_LOAD_GRACE_MS = 2000;

export type GatewayFrameState =
  | { phase: "loading" }
  | { phase: "ready"; count: number; empty: boolean }
  | { phase: "error"; reason: string; title: string }
  | { phase: "no-response" };

export interface GatewayFrame {
  /** The frame's address, or null when no gateway is configured. */
  src: string | null;
  state: GatewayFrameState;
  /** The page's own height in CSS pixels, once it has reported one. */
  height: number | null;
  /** Attach to the iframe, so only messages from that window are read. */
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Changes on every reload; use as the iframe's key so it loads afresh. */
  attempt: number;
  /** The iframe's onLoad: starts the short wait for `ready` that a loaded frame is owed. */
  onLoad: () => void;
  reload: () => void;
}

export function useGatewayFrame(page: ManagePage, serverUrl?: string): GatewayFrame {
  const theme = useResolvedTheme();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<GatewayFrameState>({ phase: "loading" });
  const [height, setHeight] = useState<number | null>(null);
  // Set by the effect below for the frame it is watching; onLoad uses it to give up early.
  const giveUp = useRef<(() => void) | null>(null);

  const src = useMemo(() => manageFrameUrl(page, { theme, serverUrl }), [page, theme, serverUrl]);

  useEffect(() => {
    setState({ phase: "loading" });
    setHeight(null);
    if (!src) return;

    // True once the page has answered (or the wait ran out), so a timer can't overwrite it.
    let settled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const clearTimers = () => {
      timers.splice(0).forEach(clearTimeout);
    };
    const noResponse = () => {
      if (settled) return;
      settled = true;
      clearTimers();
      reportEvent("mcp-gateway-frame", { page, outcome: "no-response" });
      setState({ phase: "no-response" });
    };
    timers.push(setTimeout(noResponse, FRAME_TIMEOUT_MS));
    giveUp.current = () => {
      if (!settled) timers.push(setTimeout(noResponse, FRAME_LOAD_GRACE_MS));
    };
    const settle = () => {
      settled = true;
      clearTimers();
    };

    const onMessage = (event: MessageEvent) => {
      const parsed = parseGatewayMessage(event, iframeRef.current?.contentWindow);
      // The gateway's own error page reports itself as page "error"; anything else must be
      // the page this frame asked for.
      if (!parsed || (parsed.page !== page && parsed.page !== "error")) return;
      const { message } = parsed;
      switch (message.type) {
        case "ready":
          settle();
          setState({ phase: "ready", count: message.count, empty: message.count === 0 });
          break;
        case "empty":
          settle();
          setState((current) => ({ phase: "ready", count: current.phase === "ready" ? current.count : 0, empty: true }));
          break;
        case "height":
          setHeight(message.height);
          break;
        case "error":
          settle();
          reportEvent("mcp-gateway-frame", { page, outcome: "error", reason: message.reason });
          setState({ phase: "error", reason: message.reason, title: message.title });
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      settle();
      giveUp.current = null;
      window.removeEventListener("message", onMessage);
    };
  }, [src, page, attempt]);

  const onLoad = useCallback(() => giveUp.current?.(), []);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { src, state, height, iframeRef, attempt, onLoad, reload };
}
